import React, { useState } from 'react';
import { formatAmount } from '../../utils/formatters';
import BaseModal from '../common/BaseModal';
import {
  Upload,
  Download,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import '../../styles/BillManagerV2.css';

// Example row identifier to skip during import
const EXAMPLE_ROW_IDENTIFIER = 'EXAMPLE - Electric Bill';

// Helper to parse CSV line (handles quoted values)
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"' && !inQuotes) {
      inQuotes = true;
    } else if (char === '"' && inQuotes) {
      inQuotes = false;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
};

// Helper to parse date from MM/DD/YYYY or YYYY-MM-DD format
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  // Check for MM/DD/YYYY format
  const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Check for YYYY-MM-DD format (already correct)
  const isoMatch = dateStr.match(/^\d{4}-\d{2}-\d{2}$/);
  if (isoMatch) {
    return dateStr;
  }
  
  return null;
};

function BulkImportModal({ 
  isOpen, 
  onClose, 
  onImport, 
  paymentAccounts = [], 
  members = [], 
  categories = [],
  isInHousehold = false
}) {
  const [importData, setImportData] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // 'upload', 'preview', 'result'
  const [importResults, setImportResults] = useState({ success: 0, failed: 0, errors: [] });

  const resetImportModal = () => {
    setImportData([]);
    setImportErrors([]);
    setImportWarnings([]);
    setImportStep('upload');
    setImportResults({ success: 0, failed: 0, errors: [] });
  };

  const handleClose = () => {
    resetImportModal();
    onClose();
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportErrors([]);
    setImportWarnings([]);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        // Filter out empty lines and comment lines (starting with #)
        const lines = text.split('\n').filter(line => {
          const trimmed = line.trim();
          return trimmed && !trimmed.startsWith('#');
        });
        
        if (lines.length < 2) {
          setImportErrors(['File must contain a header row and at least one data row']);
          return;
        }

        // Parse header
        const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/["']/g, ''));
        
        // Required fields
        const requiredFields = ['bill_name', 'amount'];
        const missingFields = requiredFields.filter(f => !header.includes(f));
        
        if (missingFields.length > 0) {
          setImportErrors([`Missing required columns: ${missingFields.join(', ')}`]);
          return;
        }

        // Parse data rows
        const data = [];
        const errors = [];
        const warnings = [];
        let skippedExample = false;
        
        // Valid frequency values
        const validFrequencies = ['weekly', 'biweekly', 'monthly', 'quarterly', 'annually', 'one-time'];

        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          const row = {};
          
          header.forEach((col, idx) => {
            row[col] = values[idx]?.trim() || '';
          });

          // Skip the example row
          if (row.bill_name === EXAMPLE_ROW_IDENTIFIER || row.bill_name?.startsWith('EXAMPLE - ')) {
            skippedExample = true;
            continue;
          }

          // Validate row
          if (!row.bill_name) {
            errors.push(`Row ${i + 1}: Missing bill name`);
            continue;
          }
          if (!row.amount || isNaN(parseFloat(row.amount))) {
            errors.push(`Row ${i + 1}: Invalid amount`);
            continue;
          }

          // Look up payment account by name
          const paymentAccountName = row.payment_account?.toLowerCase().trim();
          const matchedAccount = paymentAccountName 
            ? paymentAccounts.find(acc => acc.account_name.toLowerCase() === paymentAccountName)
            : null;
          
          // Warn if payment account specified but not found
          if (paymentAccountName && !matchedAccount) {
            warnings.push(`Row ${i + 1} (${row.bill_name}): Payment account "${row.payment_account}" not found`);
          }

          // Look up assigned_to by name (for household bills)
          const assignedToName = row.assigned_to?.toLowerCase().trim();
          let matchedMember = null;
          if (assignedToName && members.length > 0) {
            matchedMember = members.find(m => {
              const fullName = `${m.first_name} ${m.last_name}`.toLowerCase();
              const firstName = m.first_name.toLowerCase();
              return fullName === assignedToName || firstName === assignedToName;
            });
          }
          
          // Warn if assigned_to specified but not found
          if (assignedToName && !matchedMember) {
            if (members.length === 0) {
              warnings.push(`Row ${i + 1} (${row.bill_name}): Cannot assign "${row.assigned_to}" - not in a household`);
            } else {
              warnings.push(`Row ${i + 1} (${row.bill_name}): Member "${row.assigned_to}" not found`);
            }
          }
          
          // Validate category exists
          const categoryName = row.category?.trim();
          if (categoryName) {
            const matchedCategory = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase());
            if (!matchedCategory) {
              warnings.push(`Row ${i + 1} (${row.bill_name}): Category "${categoryName}" not found - will be created as text`);
            }
          }

          // Parse frequency
          const frequency = (row.frequency || row.billing_frequency || 'monthly').toLowerCase();
          const frequencyInterval = parseInt(row.frequency_interval) || null;
          
          // Validate frequency
          if (row.frequency && !validFrequencies.includes(frequency)) {
            warnings.push(`Row ${i + 1} (${row.bill_name}): Invalid frequency "${row.frequency}" - defaulting to monthly`);
          }
          
          // Build the final billing_frequency value
          let billingFrequency = frequency;
          if (frequencyInterval && frequencyInterval > 1) {
            // Convert to custom format like "2-weekly", "3-monthly"
            if (['week', 'weekly'].includes(frequency)) {
              billingFrequency = `${frequencyInterval}-weekly`;
            } else if (['month', 'monthly'].includes(frequency)) {
              billingFrequency = `${frequencyInterval}-monthly`;
            } else if (['year', 'yearly', 'annual', 'annually'].includes(frequency)) {
              billingFrequency = `${frequencyInterval}-yearly`;
            }
          }

          // Map to bill object
          // Default start_date to today if not provided
          const today = new Date().toISOString().split('T')[0];
          data.push({
            bill_name: row.bill_name,
            amount: parseFloat(row.amount),
            amount_type: row.amount_type?.toLowerCase() === 'variable' ? 'variable' : 'fixed',
            category: row.category || '',
            billing_frequency: billingFrequency,
            auto_pay: row.auto_pay?.toLowerCase() === 'true' || row.auto_pay === '1',
            is_household: row.is_household?.toLowerCase() === 'true' || row.is_household === '1' || row.type?.toLowerCase() === 'household',
            payment_account_id: matchedAccount?.id || null,
            assigned_to: matchedMember?.id || null,
            start_date: parseDate(row.start_date) || today,
            end_date: parseDate(row.end_date),
            notes: row.notes || '',
            url: row.url || row.website || ''
          });
        }

        if (skippedExample) {
          // Don't show as error, just info
          console.log('Skipped example row from template');
        }

        setImportData(data);
        setImportErrors(errors);
        setImportWarnings(warnings);
        
        if (data.length > 0) {
          setImportStep('preview');
        } else if (errors.length === 0) {
          setImportErrors(['No valid bills found in the file. Make sure to add your data below the example row.']);
        }
      } catch (err) {
        console.error('Parse error:', err);
        setImportErrors(['Failed to parse file. Please ensure it is a valid CSV.']);
      }
    };
    reader.readAsText(file);
  };

  const handleImportSubmit = async () => {
    setImportLoading(true);
    const results = { success: 0, failed: 0, errors: [] };

    for (let i = 0; i < importData.length; i++) {
      const bill = importData[i];
      try {
        // Backend expects camelCase field names
        await onImport({
          billName: bill.bill_name,
          amount: bill.amount,
          amountType: bill.amount_type,
          category: bill.category,
          billingFrequency: bill.billing_frequency,
          autoPay: bill.auto_pay,
          isHouseholdBill: bill.is_household && isInHousehold,
          paymentAccountId: bill.payment_account_id,
          assignedTo: bill.assigned_to,
          startDate: bill.start_date,
          endDate: bill.end_date,
          notes: bill.notes,
          url: bill.url
        });
        results.success++;
      } catch (err) {
        results.failed++;
        results.errors.push(`${bill.bill_name}: ${err.response?.data?.error || 'Failed to create'}`);
      }
    }

    setImportResults(results);
    setImportStep('result');
    setImportLoading(false);
  };

  const downloadTemplate = () => {
    const headers = 'bill_name,amount,amount_type,category,frequency,frequency_interval,auto_pay,is_household,payment_account,assigned_to,start_date,end_date,notes,url';
    const exampleRow = 'EXAMPLE - Electric Bill,150.00,variable,Utilities,monthly,,false,true,Chase Checking,John Smith,01/15/2025,12/31/2025,Monthly electric bill,https://example.com/pay';
    const content = `${headers}\n${exampleRow}\n`;
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bills_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <BaseModal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="Import Bills" 
      icon={<Upload size={24} />}
      size="xlarge"
    >
      <div className="modal-body">
        {importStep === 'upload' && (
          <div className="import-upload-step">
            <div className="import-instructions">
              <p>Upload a CSV file to import multiple bills at once.</p>
              <button className="btn-text-link" onClick={downloadTemplate}>
                <Download size={16} /> Download template
              </button>
            </div>
            
            <div className="import-dropzone">
              <input
                type="file"
                accept=".csv"
                onChange={handleImportFileChange}
                id="import-file-input"
                className="import-file-input"
              />
              <label htmlFor="import-file-input" className="import-dropzone-label">
                <Upload size={32} />
                <span>Click to select a CSV file</span>
                <span className="import-dropzone-hint">or drag and drop</span>
              </label>
            </div>

            {importErrors.length > 0 && (
              <div className="import-errors">
                <AlertCircle size={16} />
                <ul>
                  {importErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            <div className="import-field-info">
              <h4>Required fields:</h4>
              <ul>
                <li><code>bill_name</code> - Name of the bill</li>
                <li><code>amount</code> - Amount (number, without $ symbol)</li>
              </ul>
              <h4>Optional fields:</h4>
              <ul>
                <li><code>amount_type</code> - fixed or variable (default: fixed)</li>
                <li><code>category</code> - Category name</li>
                <li><code>frequency</code> - weekly, biweekly, monthly, quarterly, annually, one-time</li>
                <li><code>frequency_interval</code> - Custom repeat interval (e.g., 2 for every 2 weeks/months)</li>
                <li><code>auto_pay</code> - true/false</li>
                <li><code>is_household</code> - true/false</li>
                <li><code>payment_account</code> - Payment account name</li>
                <li><code>assigned_to</code> - Member name (first name or full name) for household bills</li>
                <li><code>start_date</code> - Start date (MM/DD/YYYY)</li>
                <li><code>end_date</code> - End date (MM/DD/YYYY)</li>
                <li><code>notes</code> - Additional notes</li>
                <li><code>url</code> - Website URL</li>
              </ul>
              <h4>Notes:</h4>
              <ul>
                <li>Lines starting with <code>#</code> are treated as comments and ignored</li>
                <li>Example rows (starting with "EXAMPLE - ") are automatically skipped</li>
                <li>Download the template for a pre-formatted file with instructions</li>
              </ul>
            </div>
          </div>
        )}

        {importStep === 'preview' && (
          <div className="import-preview-step">
            <p className="import-preview-summary">
              Found <strong>{importData.length}</strong> bill{importData.length !== 1 ? 's' : ''} to import
              {importErrors.length > 0 && (
                <span className="import-warning"> ({importErrors.length} row{importErrors.length !== 1 ? 's' : ''} skipped due to errors)</span>
              )}
            </p>
            
            <div className="import-preview-table">
              <table>
                <thead>
                  <tr>
                    <th>Bill Name</th>
                    <th>Amount</th>
                    <th>Category</th>
                    <th>Frequency</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {importData.slice(0, 10).map((bill, i) => (
                    <tr key={i}>
                      <td>{bill.bill_name}</td>
                      <td>{formatAmount(bill.amount)}</td>
                      <td>{bill.category || '-'}</td>
                      <td>{bill.billing_frequency}</td>
                      <td>
                        <span className={`type-badge ${bill.is_household ? 'household' : 'personal'}`}>
                          {bill.is_household ? 'Household' : 'Personal'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importData.length > 10 && (
                <p className="import-preview-more">...and {importData.length - 10} more</p>
              )}
            </div>

            {importErrors.length > 0 && (
              <div className="import-errors">
                <AlertCircle size={16} />
                <span>Skipped rows:</span>
                <ul>
                  {importErrors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                  {importErrors.length > 5 && <li>...and {importErrors.length - 5} more</li>}
                </ul>
              </div>
            )}

            {importWarnings.length > 0 && (
              <div className="import-warnings">
                <AlertCircle size={16} />
                <span>Warnings ({importWarnings.length}):</span>
                <ul>
                  {importWarnings.slice(0, 8).map((warn, i) => <li key={i}>{warn}</li>)}
                  {importWarnings.length > 8 && <li>...and {importWarnings.length - 8} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {importStep === 'result' && (
          <div className="import-result-step">
            <div className={`import-result-summary ${importResults.failed > 0 ? 'has-errors' : 'success'}`}>
              <CheckCircle size={48} />
              <h3>Import Complete</h3>
              <p>
                <strong>{importResults.success}</strong> bill{importResults.success !== 1 ? 's' : ''} imported successfully
                {importResults.failed > 0 && (
                  <span className="import-failed">, <strong>{importResults.failed}</strong> failed</span>
                )}
              </p>
            </div>

            {importResults.errors.length > 0 && (
              <div className="import-errors">
                <AlertCircle size={16} />
                <span>Failed imports:</span>
                <ul>
                  {importResults.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="modal-footer">
        {importStep === 'upload' && (
          <button className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        )}
        {importStep === 'preview' && (
          <>
            <button className="btn btn-secondary" onClick={resetImportModal}>
              Back
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleImportSubmit}
              disabled={importLoading}
            >
              {importLoading ? 'Importing...' : `Import ${importData.length} Bill${importData.length !== 1 ? 's' : ''}`}
            </button>
          </>
        )}
        {importStep === 'result' && (
          <button className="btn btn-primary" onClick={handleClose}>
            Done
          </button>
        )}
      </div>
    </BaseModal>
  );
}

export default BulkImportModal;
